import { Component, HostListener, OnDestroy, OnInit, Renderer2, DOCUMENT, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet
} from '@angular/router';
import { environment } from '@mogenius/environments/environment';
import { NavigationService } from '@mogenius/shared/services/navigation/navigation.service';
import { UserService } from '@mogenius/user/services/user/user.service';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { polyfill } from 'smoothscroll-polyfill';
import { BaseSubscription } from './core/components/base-subscription';
import { AppCommandsService } from './core/services/app-commands.service';
import { CommandPaletteNavigationSearchService } from './core/services/command-palette-navigation-search.service';
import { CommandPaletteResourceSearchService } from './core/services/command-palette-resource-search.service';
import { canUseCommandPalette, CommandPaletteService } from './core/services/command-palette.service';
import { CommandPaletteComponent } from './modules/shared/components/command-palette/command-palette.component';
import { NotificationBannerListComponent } from './modules/shared/components/notification-banner-list/notification-banner-list.component';
import { OrganizationService } from '@mogenius/organization/services/organization-service/organization.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';

@Component({
  selector: 'mo-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, NotificationBannerListComponent, CommandPaletteComponent, ConfirmDialog]
})
export class AppComponent extends BaseSubscription implements OnInit, OnDestroy {
  private readonly _router = inject(Router);
  private readonly _userService = inject(UserService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _dialogService = inject(DialogService);
  private readonly _navigationService = inject(NavigationService);
  private readonly _commandPalette = inject(CommandPaletteService);
  private readonly _appCommands = inject(AppCommandsService);
  private readonly _resourceSearch = inject(CommandPaletteResourceSearchService);
  private readonly _navigationSearch = inject(CommandPaletteNavigationSearchService);
  private readonly _organizationService = inject(OrganizationService);
  private renderer = inject(Renderer2);
  private readonly _document = inject<Document>(DOCUMENT);

  /**
   * Keeps the app-shell loader from index.html on screen until the first route
   * has painted.
   *
   * Angular clears the contents of `mo-root` when it creates this component,
   * which happens before the router has even started. Everything the initial
   * navigation does — the lazy chunks, the guards, several sequential waves of
   * resolvers — then runs against an empty router-outlet, and on a slow link
   * that is the longest stretch of the whole load with nothing on screen.
   *
   * The route progress bar cannot cover it: it lives inside StudioTopBar, which
   * this very navigation creates, so it is born once the wait is over.
   *
   * The markup below reuses the `.mo-boot` rules that are already inline in
   * index.html, so the same ring keeps spinning from the first byte to the
   * first content without a second visual language.
   */
  protected readonly booting = signal(true);

  // Same gate as the top-bar icon, the shortcut and the command registration.
  private readonly _paletteAvailable$ = combineLatest([
    this._organizationService.selectedOrganization$,
    this._userService.user$
  ]).pipe(
    map(([organization, user]) => canUseCommandPalette(organization, user)),
    distinctUntilChanged()
  );

  public readonly paletteAvailable = toSignal(this._paletteAvailable$, { initialValue: false });

  private isPaletteAvailable(): boolean {
    return canUseCommandPalette(this._organizationService.selectedOrganization$?.value, this._userService.user$?.value);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Command Palette: Cmd+K / Ctrl+K — organization admins and mogenius users inside an organization
    if ((event.metaKey || event.ctrlKey) && event.key === 'k' && this.isPaletteAvailable()) {
      event.preventDefault();
      if (!this._commandPalette.isOpen()) this._appCommands.prefetchResourceDescriptors();
      this._commandPalette.toggle();
      return;
    }

    if (this.#captureLoaded) {
      const isCmdOrAltPressed = event.metaKey || event.altKey; // macOS: Command (⌘), Windows/Linux: Alt

      // Detect Cmd/Alt + Carrot (^)
      const isB = event.key === 'b';

      if (isCmdOrAltPressed && isB) {
        event.preventDefault();
        this.createTicket();
      }

      // Detect Cmd + Shift + < (Less Than)
      if (event.key === 'Dead') {
        this.toggleTheme();
      }
    }
  }

  private _isDarkTheme = true; // Initial theme state, set to 'false' for 'light' theme

  #captureLoaded = false;

  ngOnInit(): void {
    // this.debugRouting();
    if (environment.stage !== 'prod') {
      this.loadCaptureDevScript();
    } else {
      this._subscriptions.add(
        this._userService.user$.subscribe((user) => {
          if (user?.isMoUser) {
            this.loadCaptureDevScript();
          }
        })
      );
    }

    // NavigationEnd or NavigationError, deliberately not NavigationCancel: a
    // cancelling guard redirects here, so a second navigation follows and
    // hiding on the cancel would tear the gap open again.
    this._subscriptions.add(
      this._router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd || event instanceof NavigationError),
          take(1)
        )
        .subscribe(() => this.booting.set(false))
    );

    this._navigationService.initScrollTopOnRouteChange();
    // Smooth scroll polyfill for Safari Soft scrolling
    polyfill();

    // Command Palette: register commands once, when the palette first becomes available
    this._subscriptions.add(
      this._paletteAvailable$
        .pipe(
          filter((available) => available),
          take(1)
        )
        .subscribe(() => {
          this._appCommands.init(() => this.toggleTheme());
          this._resourceSearch.init();
          this._navigationSearch.init();
        })
    );

    // Close the palette as soon as it is no longer available (organization left, or one without access)
    this._subscriptions.add(
      this._paletteAvailable$.subscribe((available) => {
        if (!available && this._commandPalette.isOpen()) {
          this._commandPalette.close();
        }
      })
    );

    // Route change
    this._subscriptions.add(
      this._router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          distinctUntilChanged((a: any, b: any) => {
            return a?.url?.split('?')[0] === b?.url?.split('?')[0];
          })
        )
        .subscribe((data: any) => {
          // Close any open dialog on page change
          this._closeAllDialogs();
        })
    );

    // Set meta tag on page page
    // const host = `${this._document.location.protocol}//${this._document.location.host}`;
    // this._subscriptions.add(
    //   this._router.events
    //     .pipe(
    //       filter((event) => event instanceof NavigationEnd),
    //       map(() => this.getHeaderClasses() as any),
    //       tap((route: ActivatedRouteSnapshot) => this._internationalizationService.currentRoute.next(route)),
    //       map((route: ActivatedRouteSnapshot) => route?.data ?? null),
    //       filter((data) => !!data)
    //     )
    //     .subscribe((data) => {
    //       this._internationalizationService.setPageHeaders(host, data);
    //     })
    // );
  }

  private debugRouting(): void {
    this._subscriptions.add(
      this._router.events
        .pipe(
          filter((event) => event instanceof NavigationStart),
          map((event: NavigationStart) => event.url)
        )
        .subscribe((url: string) => {
          console.trace('Navigation Start:', url);
        })
    );
  }

  private loadCaptureDevScript(): void {
    if (this.#captureLoaded) return;

    // Set global captureOptions before loading the script
    (window as any).captureOptions = {
      captureKey: 'ucle702HFXGxct',
      isEnabled: false
    };

    // Dynamically create script element
    const script = document.createElement('script');
    script.src = 'https://cdn.capture.dev/capture-js/browser/latest.js';
    script.async = true;

    // Append script to document head
    document.head.appendChild(script);
    this.#captureLoaded = true;
  }

  private createTicket(): void {
    // Your custom logic here
    (window as any).Capture.setCustomContext({
      user: this._userService.user$?.value?.firstName + ' ' + this._userService.user$?.value?.lastName,
      email: this._userService.user$?.value?.email
    });
    (window as any).Capture?.open();
  }

  private getHeaderClasses(): ActivatedRouteSnapshot | null {
    let child = this._activatedRoute.firstChild;
    while (child) {
      if (child.firstChild) {
        child = child.firstChild;
      } else if (child.snapshot) {
        return child.snapshot;
      } else {
        return null;
      }
    }
    return null;
  }

  toggleTheme(): void {
    this._isDarkTheme = !this._isDarkTheme;
    const htmlElement = document.getElementsByTagName('html')[0];
    const themeAttributeValue = this._isDarkTheme ? 'dark' : 'light';
    this.renderer.setAttribute(htmlElement, 'data-bs-theme', themeAttributeValue);
  }

  // PrimeNG has no closeAll(), but DialogService keeps every open dialog in a
  // public map, so the refs can be closed one by one. Without this a dialog
  // would stay on screen when the form inside it navigates away.
  private _closeAllDialogs(): void {
    for (const ref of Array.from(this._dialogService.dialogComponentRefMap.keys())) {
      ref.close();
    }
  }
}
